import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useStore } from "effector-react";

import { $wizard } from "../../../entities/wizard/store";
import { $bookingProcess } from "../../../entities/booking-process/store";
import { useGetCategoryName } from "../../hooks/use-get-category-name";
import { useWorkflowManifest } from "./use-workflow-manifest";
import { routePaths } from "../../configs";
import type { StepManifestEntry } from "../../types/workflow-schema";

/**
 * Schema-driven replacement for useServiceSteps.
 * Reads step list from the workflow manifest instead of static wizard store.
 *
 * Returns the same interface as the original useServiceSteps for compatibility:
 * { category, categorySteps, current, lastReachedStep, nextRoute, totalSteps }
 */
export const useDynamicSteps = () => {
  const category = useGetCategoryName();
  const { manifest, isManifestReady } = useWorkflowManifest();
  const wizardState = useStore($wizard);
  const bookingProcessState = useStore($bookingProcess);
  const location = useLocation();

  // Compose the full step list: shared entry + category steps + shared exit.
  // When no category is selected (e.g. on /get-started itself), still include
  // the shared steps so the current route can be resolved.
  const manifestSteps = useMemo((): StepManifestEntry[] => {
    if (!manifest) return [];
    const categorySteps = category?.slug
      ? manifest.serviceCategories[category.slug] || []
      : [];
    const before = manifest.sharedSteps?.getStarted
      ? [manifest.sharedSteps.getStarted]
      : [];
    // confirmation is now embedded per-category in serviceCategories, not in sharedSteps
    return [...before, ...categorySteps];
  }, [manifest, category?.slug]);

  // Keep wizard skip flags cleared for all manifest steps (no CMS-driven skip conditions)
  useEffect(() => {
    // No-op: skip conditions removed. Step skipping is handled programmatically
    // in app-entry/view.tsx via wizardApi.setSkip() for feature-flag-based cases.
  }, []);

  // Filter out skipped steps (matching the original wizard store behavior)
  const categorySteps = useMemo(() => {
    return manifestSteps
      .filter((step) => {
        const wizardStep = wizardState?.[category?.slug]?.[step.path];
        return !wizardStep?.skip;
      })
      .map((step) => {
        const wizardStep = wizardState?.[category?.slug]?.[step.path] || {
          valid: false,
        };
        return [step.path, wizardStep] as [string, typeof wizardStep];
      });
  }, [manifestSteps, wizardState, category?.slug]);

  // Build a path→stepId lookup from the manifest
  const pathToStepId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const step of manifestSteps) {
      map[step.path] = step.stepId;
    }
    return map;
  }, [manifestSteps]);

  // Find current step by matching URL pathname
  const current = useMemo(() => {
    const pathname = location.pathname.replace(/\/$/, ""); // Normalize trailing slash
    const index = categorySteps.findIndex(
      ([stepKey]) => stepKey.replace(/\/$/, "") === pathname,
    );

    if (index === -1) return undefined;

    return {
      index,
      key: categorySteps[index][0],
      state: categorySteps[index][1],
    };
  }, [location.pathname, categorySteps]);

  // Resolve the current step's stepId for config fetching
  const currentStepId = useMemo(() => {
    if (!current) return undefined;
    return pathToStepId[current.key];
  }, [current, pathToStepId]);

  // Find the last step the user can access (first unsubmitted step)
  const lastReachedStep = useMemo(() => {
    const lastIndex = categorySteps.findIndex(([, state]) => {
      return !state.submitted;
    });

    if (lastIndex === -1) return undefined;

    return {
      index: lastIndex,
      key: categorySteps[lastIndex][0],
      state: categorySteps[lastIndex][1],
    };
  }, [categorySteps]);

  // Calculate next route
  const nextRoute = useMemo(() => {
    if (!current) return routePaths.getStarted;

    const nextIndex = current.index + 1;
    let nextStep: string;

    if (nextIndex >= categorySteps.length) {
      nextStep = routePaths.confirmation;
    } else {
      nextStep = categorySteps[nextIndex][0];
    }

    const categoryParam =
      bookingProcessState.selectedServices?.join(",") || category?.slug;
    return `${nextStep}?category=${categoryParam}`;
  }, [
    current,
    categorySteps,
    bookingProcessState.selectedServices,
    category?.slug,
  ]);

  const totalSteps = categorySteps.length; // confirmation is now an explicit step in the manifest

  return {
    category,
    categorySteps,
    current,
    currentStepId,
    isManifestReady,
    lastReachedStep,
    nextRoute,
    totalSteps,
  };
};
